import {ReactElement, ReactNode, useState} from "react";
import {Box, ButtonBase, Checkbox, Divider, FormControlLabel, IconButton, Menu, MenuItem, Select, Tooltip, Typography} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

// Reusable primitives for the MS-ribbon-style menu bar: dense, grouped controls
// laid out under a horizontal tab strip.

// A labelled group of controls with a caption underneath, separated from
// neighbouring groups by a vertical divider.
export function RibbonGroup(props: {
    label: string;
    children: ReactNode;
}) {
    return (
        <Box sx={{display: "flex", alignItems: "stretch", flexShrink: 0}}>
            <Box sx={{display: "flex", flexDirection: "column", height: "100%"}}>
                <Box sx={{
                    display: "flex",
                    flex: 1,
                    alignItems: "flex-start",
                    gap: 0.5,
                    px: 0.5,
                }}>
                    {props.children}
                </Box>
                <Typography sx={{
                    textAlign: "center",
                    color: "text.secondary",
                    fontSize: 10,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    pt: 0.5,
                    pb: 0.25,
                }}>
                    {props.label}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{mx: 0.5}} />
        </Box>
    );
}

// one entry in a RibbonButton's dropdown menu.
export type RibbonDropdownItem = {
    label: string;
    onClick: () => void;
};

// A large icon-over-text button, the staple of a ribbon. Supports a selected
// (active) state for toggle-like behaviour, and an optional split dropdown: when
// `dropdown` is set, the icon still fires `onClick`, but the label row grows a
// trailing arrow and clicking it opens a menu of alternate actions.
export function RibbonButton(props: {
    icon: ReactNode;
    label: string;
    active?: boolean;
    disabled?: boolean;
    // the key that triggers this action; shown as a hover tooltip (e.g. 'Press "V"').
    shortcut?: string;
    onClick: () => void;
    // when provided (and non-empty), the label section becomes a dropdown trigger.
    dropdown?: RibbonDropdownItem[];
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const hasDropdown = !!props.dropdown && props.dropdown.length > 0;

    const withTooltip = (el: ReactElement) =>
        props.shortcut ? (
            <Tooltip title={`Press "${props.shortcut}"`} arrow>{el}</Tooltip>
        ) : el;

    if (!hasDropdown) {
        const button = (
            <ButtonBase
                disabled={props.disabled}
                onClick={props.onClick}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 0.25,
                    px: 0.75,
                    pt: 0.5,
                    pb: 0.25,
                    minWidth: 60,
                    height: "100%",
                    borderRadius: 1,
                    color: props.active ? "primary.main" : "text.primary",
                    bgcolor: props.active ? "action.selected" : "transparent",
                    "&:hover": {bgcolor: "action.hover"},
                    "&.Mui-disabled": {opacity: 0.4},
                }}
            >
                <Box sx={{display: "flex", "& svg": {fontSize: 26}}}>{props.icon}</Box>
                <Typography sx={{fontSize: 11, lineHeight: 1.1, textAlign: "center", whiteSpace: "nowrap"}}>
                    {props.label}
                </Typography>
            </ButtonBase>
        );

        return withTooltip(button);
    }

    // split variant: an icon button on top (primary action) and a label+arrow
    // button below (opens the menu). Wrapped in a surface that carries the shared
    // active/disabled styling so the two halves read as one control.
    const surface = (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                minWidth: 60,
                height: "100%",
                borderRadius: 1,
                overflow: "hidden",
                color: props.active ? "primary.main" : "text.primary",
                bgcolor: props.active ? "action.selected" : "transparent",
                opacity: props.disabled ? 0.4 : 1,
            }}
        >
            <ButtonBase
                disabled={props.disabled}
                onClick={props.onClick}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    px: 0.75,
                    pt: 0.5,
                    "&:hover": {bgcolor: "action.hover"},
                }}
            >
                <Box sx={{display: "flex", "& svg": {fontSize: 26}}}>{props.icon}</Box>
            </ButtonBase>
            <ButtonBase
                disabled={props.disabled}
                onClick={e => setAnchorEl(e.currentTarget)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.5,
                    pt: 0.25,
                    pb: 0.25,
                    "&:hover": {bgcolor: "action.hover"},
                }}
            >
                <Typography sx={{fontSize: 11, lineHeight: 1.1, textAlign: "center", whiteSpace: "nowrap"}}>
                    {props.label}
                </Typography>
                <ArrowDropDownIcon sx={{fontSize: 16, ml: -0.25, mr: -0.5}} />
            </ButtonBase>
        </Box>
    );

    return (
        <>
            {withTooltip(surface)}
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                transformOrigin={{vertical: "top", horizontal: "center"}}
            >
                {props.dropdown!.map((item, i) => (
                    <MenuItem
                        key={i}
                        onClick={() => {
                            setAnchorEl(null);
                            item.onClick();
                        }}
                        sx={{fontSize: 13}}
                    >
                        {item.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

// A small icon-beside-text button, for stacking two or three in a RibbonStack where
// RibbonButtons would take too much width.
export function RibbonSmallButton(props: {
    icon: ReactNode;
    label: string;
    active?: boolean;
    disabled?: boolean;
    // the key that triggers this action; shown as a hover tooltip (e.g. 'Press "S"').
    shortcut?: string;
    onClick: () => void;
}) {
    const button = (
        <ButtonBase
            disabled={props.disabled}
            onClick={props.onClick}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.5,
                px: 0.5,
                py: 0.25,
                borderRadius: 1,
                color: props.active ? "primary.main" : "text.primary",
                bgcolor: props.active ? "action.selected" : "transparent",
                "&:hover": {bgcolor: "action.hover"},
                "&.Mui-disabled": {opacity: 0.4},
                "& svg": {fontSize: 16},
            }}
        >
            {props.icon}
            <Typography sx={{fontSize: 12, lineHeight: 1.2, whiteSpace: "nowrap"}}>
                {props.label}
            </Typography>
        </ButtonBase>
    );

    return props.shortcut ? (
        <Tooltip title={`Press "${props.shortcut}"`} arrow>
            <span style={{display: "flex"}}>{button}</span>
        </Tooltip>
    ) : button;
}

// one half of a RibbonVSplitButton. `label` and `shortcut` show as the hover tooltip.
export type RibbonSplitHalf = {
    icon: ReactNode;
    label: string;
    shortcut?: string;
    onClick: () => void;
};

// A button the size of a RibbonButton, split into a top and a bottom action (icons only),
// with one caption under both (e.g. Insert above / below).
export function RibbonVSplitButton(props: {
    top: RibbonSplitHalf;
    bottom: RibbonSplitHalf;
    label: string;
    disabled?: boolean;
}) {
    const half = (h: RibbonSplitHalf) => (
        <Tooltip title={h.shortcut ? `${h.label} (Press "${h.shortcut}")` : h.label} arrow>
            <span style={{display: "flex"}}>
                <ButtonBase
                    disabled={props.disabled}
                    onClick={h.onClick}
                    sx={{
                        display: "flex",
                        flex: 1,
                        justifyContent: "center",
                        borderRadius: 1,
                        "&:hover": {bgcolor: "action.hover"},
                        "& svg": {fontSize: 20},
                    }}
                >
                    {h.icon}
                </ButtonBase>
            </span>
        </Tooltip>
    );

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            minWidth: 60,
            height: "100%",
            px: 0.75,
            pt: 0.25,
            pb: 0.25,
            color: "text.primary",
            opacity: props.disabled ? 0.4 : 1,
        }}>
            {half(props.top)}
            {half(props.bottom)}
            <Typography sx={{fontSize: 11, lineHeight: 1.1, textAlign: "center", whiteSpace: "nowrap"}}>
                {props.label}
            </Typography>
        </Box>
    );
}

// A small icon-only toggle for a dense option set (e.g. the six glazing positions).
// The label shows as a hover tooltip instead of under the icon, so a row of these
// takes a fraction of the width of RibbonButtons.
export function RibbonIconButton(props: {
    icon: ReactNode;
    label: string;
    active?: boolean;
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <Tooltip title={props.label} arrow>
            <span style={{display: "flex", alignSelf: "center"}}>
                <ButtonBase
                    disabled={props.disabled}
                    onClick={props.onClick}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 30,
                        height: 30,
                        borderRadius: 1,
                        color: props.active ? "primary.main" : "text.primary",
                        bgcolor: props.active ? "action.selected" : "transparent",
                        "&:hover": {bgcolor: "action.hover"},
                        "&.Mui-disabled": {opacity: 0.4},
                        "& svg": {fontSize: 22},
                    }}
                >
                    {props.icon}
                </ButtonBase>
            </span>
        </Tooltip>
    );
}

// lays RibbonIconButtons out in rows of `columns`, for an option set too wide as one
// row.
export function RibbonIconGrid(props: {columns: number; children: ReactNode}) {
    return (
        <Box sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${props.columns}, auto)`,
            gap: 0.25,
            alignSelf: "center",
        }}>
            {props.children}
        </Box>
    );
}

// A compact stacked column of small controls (e.g. checkboxes/toggles) for the
// smaller items in a group.
export function RibbonStack(props: {children: ReactNode}) {
    return (
        <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.25, height: "100%"}}>
            {props.children}
        </Box>
    );
}

// A compact -/+ number stepper for a ribbon group (e.g. the hinge count). The
// caller owns the value; the buttons clamp against optional min/max bounds.
export function RibbonStepper(props: {
    value: number;
    min?: number;
    max?: number;
    onChange: (value: number) => void;
}) {
    const atMin = props.min !== undefined && props.value <= props.min;
    const atMax = props.max !== undefined && props.value >= props.max;

    return (
        <Box sx={{display: "flex", alignItems: "center", height: "100%", px: 0.5, gap: 0.25}}>
            <IconButton
                size="small"
                sx={{p: 0.25}}
                disabled={atMin}
                onClick={() => props.onChange(props.value - 1)}
            >
                <RemoveIcon sx={{fontSize: 18}} />
            </IconButton>
            <Typography sx={{fontSize: 15, minWidth: 20, textAlign: "center"}}>
                {props.value}
            </Typography>
            <IconButton
                size="small"
                sx={{p: 0.25}}
                disabled={atMax}
                onClick={() => props.onChange(props.value + 1)}
            >
                <AddIcon sx={{fontSize: 18}} />
            </IconButton>
        </Box>
    );
}

// A compact dropdown (single-select) for a ribbon group, e.g. picking the frame
// profile. The caller owns the value; the group caption (RibbonGroup label) names it.
export function RibbonSelect<T extends string>(props: {
    value: T;
    options: {value: T; label: string}[];
    onChange: (value: T) => void;
    disabled?: boolean;
    // inside a RibbonStack: no vertical centering of its own, tighter padding so two
    // fit in the ribbon height.
    stacked?: boolean;
}) {
    return (
        <Box sx={props.stacked ? {display: "flex"} : {display: "flex", alignItems: "center", height: "100%", px: 0.5}}>
            <Select
                size="small"
                value={props.value}
                disabled={props.disabled}
                onChange={e => props.onChange(e.target.value as T)}
                sx={{fontSize: 13, "& .MuiSelect-select": {py: props.stacked ? 0.25 : 0.5}}}
            >
                {props.options.map(o => (
                    <MenuItem key={o.value} value={o.value} sx={{fontSize: 13}}>
                        {o.label}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

export function RibbonCheckbox(props: {
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}) {
    return (
        <FormControlLabel
            sx={{m: 0, "& .MuiFormControlLabel-label": {fontSize: 12}}}
            control={
                <Checkbox
                    size="small"
                    sx={{p: 0.5}}
                    checked={props.checked}
                    disabled={props.disabled}
                    onChange={e => props.onChange(e.target.checked)}
                />
            }
            label={props.label}
        />
    );
}
